import { FC } from 'react';

import { EditModalState } from '../utils/delivery.types';

interface Props {
  modal: EditModalState;
  setModal: React.Dispatch<React.SetStateAction<EditModalState | null>>;
  onSave: () => void;
  onRevert: () => void;
}

export const DeliveredEditModal: FC<Props> = ({ modal, setModal, onSave, onRevert }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={() => !modal.isSaving && setModal(null)}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">배송완료 수정</h3>
          <p className="mt-1 text-xs text-gray-500">{modal.itemName}</p>
          <p className="text-xs text-gray-500">{modal.address}</p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              배송완료 일시
            </label>
            <input
              type="datetime-local"
              value={modal.deliveredAt}
              onChange={(e) =>
                setModal((prev) =>
                  prev ? { ...prev, deliveredAt: e.target.value } : null,
                )
              }
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              배송완료 사진
            </label>
            {(modal.imagePreview || modal.existingImageUrl) && (
              <div className="relative mb-2">
                <img
                  src={modal.imagePreview || modal.existingImageUrl || ''}
                  alt="배송완료 사진"
                  className="h-48 w-full rounded-lg border object-contain"
                />
                {modal.imagePreview && (
                  <button
                    onClick={() =>
                      setModal((prev) =>
                        prev ? { ...prev, imageFile: null, imagePreview: null } : null,
                      )
                    }
                    className="absolute right-1 top-1 rounded-full bg-red-500 p-0.5 text-white shadow"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    setModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            imageFile: file,
                            imagePreview: ev.target?.result as string,
                          }
                        : null,
                    );
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="w-full text-sm text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
        </div>

        <div className="border-t bg-red-50/50 px-5 py-3">
          {!modal.showRevertConfirm ? (
            <button
              onClick={() =>
                setModal((prev) => (prev ? { ...prev, showRevertConfirm: true } : null))
              }
              disabled={modal.isSaving}
              className="text-xs text-red-500 underline hover:text-red-700 disabled:opacity-50"
            >
              배송완료를 취소하고 배송중으로 원복
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-red-700">
                원복 시 수령인에게 재알림이 발송될 수 있습니다.
              </p>
              <p className="text-xs text-red-600">
                확인을 위해 아래에 <span className="font-bold">원복</span>을 입력해주세요.
              </p>
              <input
                type="text"
                value={modal.revertConfirmText}
                onChange={(e) =>
                  setModal((prev) =>
                    prev ? { ...prev, revertConfirmText: e.target.value } : null,
                  )
                }
                placeholder="원복"
                className="w-full rounded border border-red-300 px-3 py-1.5 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            showRevertConfirm: false,
                            revertConfirmText: '',
                          }
                        : null,
                    )
                  }
                  disabled={modal.isSaving}
                  className="rounded border px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={onRevert}
                  disabled={modal.revertConfirmText !== '원복' || modal.isSaving}
                  className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {modal.isSaving ? '처리중...' : '배송중으로 원복'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button
            onClick={() => setModal(null)}
            disabled={modal.isSaving}
            className="rounded-lg border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={onSave}
            disabled={modal.isSaving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {modal.isSaving ? '저장중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};
